import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { StateProvider } from './contexts/StateContext';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import FlashcardApp from './pages/FlashcardApp';
import Profile from './pages/Profile';
import Checkout from './pages/Checkout';
import Disclaimer from './pages/Disclaimer';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ContactUs from './pages/ContactUs';
import HelpCenter from './pages/HelpCenter';
import LicensingGuide from './pages/LicensingGuide';
import BlogPost from './pages/BlogPost';
import Resources from './pages/Resources';
import FloridaRealEstateExam from './pages/FloridaRealEstateExam';
import GeorgiaRealEstateExam from './pages/GeorgiaRealEstateExam';
import CaliforniaRealEstateExam from './pages/CaliforniaRealEstateExam';
import NewYorkRealEstateExam from './pages/NewYorkRealEstateExam';
import NewJerseyRealEstateExam from './pages/NewJerseyRealEstateExam';
import PracticeTest from './components/PracticeTest';
import ProtectedRoute from './components/ProtectedRoute';

// Component to scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top on every route change
    window.scrollTo(0, 0);
    
    // Also reset scroll position for body and html elements
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    
    // Clear any hash fragments that might cause scrolling
    if (window.location.hash) {
      window.location.hash = '';
    }
  }, [pathname]);

  return null;
}

function App() {
  // Keep backend server warm by pinging it periodically
  useEffect(() => {
    const pingServer = async () => {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002'}/health`, {
          method: 'GET',
        });
        console.log('Server ping successful');
      } catch (error) {
        console.log('Server ping failed:', error);
      }
    };

    // Ping immediately on app load
    pingServer();

    // Then ping every 5 minutes to keep server warm
    const interval = setInterval(pingServer, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  return (
    <AuthProvider>
      <StateProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/demo" element={<FlashcardApp mode="demo" />} />
          
          {/* Legal and Support pages */}
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/help" element={<HelpCenter />} />
          
          {/* Resources and Blog posts */}
          <Route path="/resources" element={<Resources />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          
          {/* State-specific landing pages */}
          <Route path="/florida-real-estate-exam" element={<FloridaRealEstateExam />} />
          <Route path="/georgia-real-estate-exam" element={<GeorgiaRealEstateExam />} />
          <Route path="/california-real-estate-exam" element={<CaliforniaRealEstateExam />} />
          <Route path="/new-york-real-estate-exam" element={<NewYorkRealEstateExam />} />
          <Route path="/new-jersey-real-estate-exam" element={<NewJerseyRealEstateExam />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/practice-test" element={
            <ProtectedRoute>
              <PracticeTest />
            </ProtectedRoute>
          } />
          <Route path="/study" element={
            <ProtectedRoute>
              <FlashcardApp mode="full" />
            </ProtectedRoute>
          } />
          <Route path="/licensing-guide" element={
            <ProtectedRoute>
              <LicensingGuide />
            </ProtectedRoute>
          } />
          <Route path="/checkout" element={<Checkout />} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      </StateProvider>
    </AuthProvider>
  );
}

export default App;
